import { RoleName } from 'constants/constants';
// Type-only: jest cannot load EpdClient at runtime (it pulls in ESM axios).
import type { MembershipModel } from 'services/EpdClient';

/**
 * What the removal confirmation dialog on User Details shows. The dialog renders it as is;
 * every text is a translation key in the account.removeMembership group.
 */
export interface RemovalDialogContent {
  /** The removal is not allowed: info icon and a single Close button. */
  blocked: boolean;
  titleKey: string;
  /** Lines between the title and the "After removal:" list. */
  paragraphKeys: string[];
  /** The "After removal:" bullets; empty when there is no list. */
  consequenceKeys: string[];
  /** The last line of the body. */
  closingKey: string | null;
  showLastRoleWarning: boolean;
}

const TAKES_EFFECT_IMMEDIATELY_KEY = 'account.removeMembership.takesEffectImmediately';
const KEY_MANAGER_TITLE_KEY = 'account.removeMembership.keyManagerTitle';
const KEY_MANAGER_REASON_KEY = 'account.removeMembership.keyManagerReason';
const KEY_MANAGER_ROLE_INSTRUCTION_KEY = 'account.removeMembership.keyManagerRoleInstruction';
const KEY_MANAGER_ORGANISATION_INSTRUCTION_KEY = 'account.removeMembership.keyManagerOrganisationInstruction';

/** Consequences of removing a verifier from their home organisation (Case 3; also the Case 6 Home Company variant). */
const HOME_VERIFIER_CONSEQUENCE_KEYS: readonly string[] = [
  'account.removeMembership.homeVerifierConsequence1',
  'account.removeMembership.homeVerifierConsequence2',
  'account.removeMembership.homeVerifierConsequence3',
];

/** Consequences of removing an invited verifier from an EPD Owner organisation (Case 4; also the Case 6 EPD Owner organisation variant). */
const OWNER_ORGANISATION_VERIFIER_CONSEQUENCE_KEYS: readonly string[] = [
  'account.removeMembership.ownerOrganisationVerifierConsequence1',
  'account.removeMembership.ownerOrganisationVerifierConsequence2',
  'account.removeMembership.ownerOrganisationVerifierConsequence3',
];

/** The account manager (Key Manager) cannot be removed; instructionKey says what to do first (Cases 2 and 7). */
const keyManagerBlockedContent = (instructionKey: string): RemovalDialogContent => ({
  blocked: true,
  titleKey: KEY_MANAGER_TITLE_KEY,
  paragraphKeys: [KEY_MANAGER_REASON_KEY],
  consequenceKeys: [],
  closingKey: instructionKey,
  showLastRoleWarning: false,
});

type RoleConfirmationText = Pick<RemovalDialogContent, 'titleKey' | 'consequenceKeys' | 'closingKey'>;

/** Only the removed membership decides, never the user's other memberships (spec FR-004). */
const getRoleConfirmationText = (removed: MembershipModel): RoleConfirmationText => {
  // Case 1
  if (removed.roleName === RoleName.EPD_OWNER) {
    return {
      titleKey: 'account.removeMembership.epdOwnerTitle',
      consequenceKeys: [],
      closingKey: TAKES_EFFECT_IMMEDIATELY_KEY,
    };
  }

  // Case 3: an unlinked verifier membership is the verifier's home organisation.
  if (removed.roleName === RoleName.EPD_VERIFIER && removed.isHomeCompany === true) {
    return {
      titleKey: 'account.removeMembership.homeVerifierTitle',
      consequenceKeys: [...HOME_VERIFIER_CONSEQUENCE_KEYS],
      closingKey: TAKES_EFFECT_IMMEDIATELY_KEY,
    };
  }

  // Case 4
  if (removed.roleName === RoleName.EPD_VERIFIER && removed.isEpdOwnerCompany === true) {
    return {
      titleKey: 'account.removeMembership.ownerOrganisationVerifierTitle',
      consequenceKeys: [...OWNER_ORGANISATION_VERIFIER_CONSEQUENCE_KEYS],
      closingKey: TAKES_EFFECT_IMMEDIATELY_KEY,
    };
  }

  // Case 5
  if (removed.roleName === RoleName.LCA_PRACTITIONER) {
    return {
      titleKey: 'account.removeMembership.lcaPractitionerTitle',
      consequenceKeys: [],
      closingKey: TAKES_EFFECT_IMMEDIATELY_KEY,
    };
  }

  // Case 8
  return {
    titleKey: 'account.removeMembership.generalTitle',
    consequenceKeys: [],
    closingKey: 'account.removeMembership.generalClosing',
  };
};

/**
 * Dialog content for removing one role (one membership) of a user in an organisation.
 * Roles are compared by name: the role GUIDs differ in casing between the portal and the API.
 *
 * @param companyMemberships all of the user's memberships in that organisation, the removed one included
 * @param removed the membership whose role chip was removed
 */
export const getRoleRemovalContent = (
  companyMemberships: MembershipModel[],
  removed: MembershipModel
): RemovalDialogContent => {
  // Case 2: the account manager keeps their EPD Owner role (isAccountManager alone ignores the role).
  if (removed.roleName === RoleName.EPD_OWNER && removed.isAccountManager === true) {
    return keyManagerBlockedContent(KEY_MANAGER_ROLE_INSTRUCTION_KEY);
  }

  return {
    blocked: false,
    paragraphKeys: [],
    ...getRoleConfirmationText(removed),
    // Last Role Warning: Cases 1, 3, 4, 5 and 8 (spec FR-005).
    showLastRoleWarning: companyMemberships.length === 1,
  };
};

/**
 * The Case 6 "After removal:" list when the user holds EPD Verifier in the organisation (spec FR-004): the Case 3
 * consequences for their Home Company, otherwise the Case 4 ones for an EPD Owner organisation. Case 3 wins when both
 * apply, as on the role chip; its consequences include the Case 4 ones.
 */
const getOrganisationVerifierConsequenceKeys = (companyMemberships: MembershipModel[]): string[] => {
  const verifierMemberships = companyMemberships.filter((m) => m.roleName === RoleName.EPD_VERIFIER);

  if (verifierMemberships.some((m) => m.isHomeCompany === true)) {
    return [...HOME_VERIFIER_CONSEQUENCE_KEYS];
  }

  if (verifierMemberships.some((m) => m.isEpdOwnerCompany === true)) {
    return [...OWNER_ORGANISATION_VERIFIER_CONSEQUENCE_KEYS];
  }

  return [];
};

/**
 * Dialog content for removing a user from an organisation (all of their memberships in it).
 * Any row of the organisation can decide, so every row is checked, not only the first one.
 *
 * @param companyMemberships all of the user's memberships in that organisation
 */
export const getOrganisationRemovalContent = (companyMemberships: MembershipModel[]): RemovalDialogContent => {
  // Case 7: the account manager who holds EPD Owner there (isAccountManager alone ignores the role).
  if (companyMemberships.some((m) => m.roleName === RoleName.EPD_OWNER && m.isAccountManager === true)) {
    return keyManagerBlockedContent(KEY_MANAGER_ORGANISATION_INSTRUCTION_KEY);
  }

  return {
    blocked: false,
    titleKey: 'account.removeMembership.organisationTitle',
    paragraphKeys: ['account.removeMembership.organisationAllRolesRemoved'],
    consequenceKeys: getOrganisationVerifierConsequenceKeys(companyMemberships),
    closingKey: TAKES_EFFECT_IMMEDIATELY_KEY,
    // The Last Role Warning is for role removal only.
    showLastRoleWarning: false,
  };
};
